import dayjs from 'dayjs';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';

/** The API prorates against a month of 30 days, whatever the calendar says. */
export const CYCLE_DAYS = 30;
const DAY_MS = 86_400_000;
const REFERENCE_YEAR = 2001;

const MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const SPANISH_DATE = /^(\d{1,2})\s+de\s+([a-záéíóúü]+)/i;

/** Bounds arrive as an ISO date or as the Spanish label the API builds ("8 de octubre"). */
const toReferenceDate = (raw: string): Date | null => {
  const match = SPANISH_DATE.exec(raw.trim());

  if (match) {
    const month = MONTHS.indexOf(match[2].toLowerCase());
    return month < 0 ? null : new Date(Date.UTC(REFERENCE_YEAR, month, Number(match[1])));
  }

  const iso = dayjs(raw);
  return iso.isValid() ? new Date(Date.UTC(REFERENCE_YEAR, iso.month(), iso.date())) : null;
};

/** Days a charge covers, or null when the bounds do not make a span. */
export const getPeriodDays = (start: string, end: string): number | null => {
  const from = toReferenceDate(start);
  const to = toReferenceDate(end);

  if (!from || !to) {
    return null;
  }

  const days = Math.round((to.getTime() - from.getTime()) / DAY_MS);
  if (days > 0) {
    return days;
  }

  // The labels carry no year, so a negative span is a crossing into January only when wrapping it
  // lands inside a cycle; anything longer is a reversed range.
  const wrapped = days + 365;
  return days < 0 && wrapped <= CYCLE_DAYS + 1 ? wrapped : null;
};

/**
 * What the charges add up to over a whole cycle, scaling the prorated ones back up and leaving the
 * ones with no period out. Null when nothing carries a readable period.
 *
 * Approximate: the API rounds each prorated charge to cents, and scaling multiplies that rounding.
 */
export const getFullCycleTotal = (stores: SubscriptionBlockViewDto['stores']): number | null => {
  let total = 0;
  let hasRecurringConcept = false;

  for (const store of stores) {
    for (const concept of store.concepts) {
      if (!concept.periodStart || !concept.periodEnd) {
        continue;
      }

      const days = getPeriodDays(concept.periodStart, concept.periodEnd);
      if (days === null) {
        return null;
      }

      total += days >= CYCLE_DAYS ? concept.value : (concept.value * CYCLE_DAYS) / days;
      hasRecurringConcept = true;
    }
  }

  return hasRecurringConcept ? total : null;
};
